import { Box, Flex, HStack, Icon, Tag, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import type { ElementType, ReactNode } from 'react';
import { PARTBAY_BLUE, PARTBAY_BLUE_TINT, SETTINGS_BORDER_COLOR } from './constants';

/**
 * The rail the cards hang off, in pixels. Mirrors the custom price match order,
 * so a rule built in steps reads the same way everywhere: everything on the
 * rail is centred against RAIL_CENTER by pulling back out of RAIL_INSET.
 */
const RAIL_INSET = 28;
const RAIL_LEFT = 13;
const RAIL_WIDTH = 2;
const RAIL_CENTER = RAIL_LEFT + RAIL_WIDTH / 2;
const BADGE_SIZE = 20;

/** Negative margin that centres a circle of `size` on the rail. */
const railCenterOffset = (size: number) => `${RAIL_CENTER - size / 2 - RAIL_INSET}px`;

export interface SettingsRuleStepValue {
    label: string;
    colorScheme?: string;
}

export interface SettingsRuleStepProps {
    index: number;
    /** How the step reads in the sentence, e.g. "If the vendor is". */
    lead: string;
    icon: ElementType;
    /** Chips for what has been chosen; empty renders the waiting-on state. */
    values: SettingsRuleStepValue[];
    /** Shown in grey when nothing has been chosen yet. */
    placeholder: string;
}

/** One step of a rule: what it decides, and what it has been set to. */
export const SettingsRuleStep = ({
    index,
    lead,
    icon,
    values,
    placeholder,
}: SettingsRuleStepProps) => {
    const isSet = values.length > 0;

    return (
        <Box position="relative">
            <HStack spacing={2} mb={2} ml={railCenterOffset(BADGE_SIZE)}>
                <Flex
                    align="center"
                    justify="center"
                    boxSize={`${BADGE_SIZE}px`}
                    borderRadius="full"
                    bg={isSet ? PARTBAY_BLUE_TINT : 'gray.100'}
                    color={isSet ? PARTBAY_BLUE : 'gray.400'}
                    fontSize="0.65rem"
                    fontWeight="bold"
                    flexShrink={0}
                >
                    {index + 1}
                </Flex>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500">
                    {lead}
                </Text>
            </HStack>

            <Box
                borderWidth="1px"
                borderColor={isSet ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                borderRadius="lg"
                bg="white"
                overflow="hidden"
            >
                <Flex
                    align="center"
                    gap={2}
                    px={3}
                    py={2}
                    bg={isSet ? PARTBAY_BLUE_TINT : 'gray.50'}
                    borderBottomWidth="1px"
                    borderColor={isSet ? PARTBAY_BLUE : SETTINGS_BORDER_COLOR}
                >
                    <Icon
                        as={icon}
                        boxSize={4}
                        color={isSet ? PARTBAY_BLUE : 'gray.400'}
                        flexShrink={0}
                    />
                    <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color={isSet ? PARTBAY_BLUE : 'gray.600'}
                        textTransform="uppercase"
                        letterSpacing="wide"
                    >
                        {lead}
                    </Text>
                </Flex>

                <Box px={4} py={3}>
                    {isSet ? (
                        <Wrap spacing={2}>
                            {values.map((value) => (
                                <WrapItem key={value.label}>
                                    <Tag
                                        size="sm"
                                        variant="subtle"
                                        colorScheme={value.colorScheme ?? 'blue'}
                                    >
                                        {value.label}
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>
                    ) : (
                        <Text fontSize="sm" color="gray.400">
                            {placeholder}
                        </Text>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export interface SettingsRuleStepsProps {
    /** SettingsRuleStep elements, in the order the rule reads. */
    children: ReactNode;
}

/**
 * The rail a rule's steps hang off, read top to bottom. Same treatment as the
 * custom price match order, because it answers the same question — what did I
 * just build, in the order PartBay reads it.
 */
export const SettingsRuleSteps = ({ children }: SettingsRuleStepsProps) => (
    <Box position="relative" pl={`${RAIL_INSET}px`}>
        <Box
            position="absolute"
            left={`${RAIL_LEFT}px`}
            top={`${BADGE_SIZE / 2}px`}
            bottom={`${BADGE_SIZE / 2}px`}
            w={`${RAIL_WIDTH}px`}
            bg={PARTBAY_BLUE}
            opacity={0.35}
        />
        <VStack align="stretch" spacing={5}>
            {children}
        </VStack>
    </Box>
);

export interface SettingsRuleSummaryProps {
    /** Heading above the sentence, e.g. "Rule summary". */
    title: string;
    children: ReactNode;
}

/** The same rule as one sentence, under the steps that built it. */
export const SettingsRuleSummary = ({ title, children }: SettingsRuleSummaryProps) => (
    <Box
        borderWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
        bg="white"
        borderRadius="md"
        px={4}
        py={3}
    >
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
            {title}
        </Text>
        {children}
    </Box>
);

export default SettingsRuleSteps;
